/**
 * The CPT codes the two claim allow-lists bill under, and whether an
 * account carries one.
 *
 * Ticking an assessment for Preventive Health Counseling or E-Visit
 * only means anything if the account lists the matching code in its ISP
 * services. E-Visit takes either of two: 99422 and 99423 are the same
 * claim at different lengths, and an account that bills one of them can
 * raise the claim.
 *
 * The codes live in the account-edit form next to
 * `ispConfiguration.services`, so this is answered from the form's own
 * values, with no request and nothing to save first.
 *
 * Only presence is checked. The fee is deliberately not part of it
 * (customer decision 2026-09-29): an account may price a code at zero
 * and still want the claim raised.
 */
import { CLAIM_ALLOW_LIST_FIELDS } from './claimAllowLists';

export const CLAIM_CPT_CODES = {
  [CLAIM_ALLOW_LIST_FIELDS.counseling]: ['99401'],
  [CLAIM_ALLOW_LIST_FIELDS.programClaim]: ['99422', '99423'],
};

const normalizeCode = value => String(value ?? '').trim();

/** The account's service rows for any of `codes`. */
export const findClaimCptServices = (services, codes) => {
  const wanted = new Set((codes || []).map(normalizeCode));
  return (Array.isArray(services) ? services : []).filter(service =>
    wanted.has(normalizeCode(service?.cptCode))
  );
};

/** True when the account carries at least one of `codes`. */
export const isClaimCptUsable = (services, codes) =>
  findClaimCptServices(services, codes).length > 0;

/**
 * How many ticks in `list` will actually be saved. `allowed` is the
 * catalogue's set of keys that can carry this claim at all
 * (`counselingKeysOf` / `automationKeysOf`); the save prunes the list
 * to it, so a warning counted from the raw list reports ticks that are
 * on their way out — which is what made an account warn about a code
 * with no tick anywhere in sight to explain it. `null` means the
 * catalogue does not report the flag, and nothing is pruned.
 */
const countEffective = (list, allowed) => {
  const entries = list || [];
  if (!allowed) return entries.length;
  return entries.filter(key => allowed.has(String(key).toLowerCase())).length;
};

/**
 * The claim codes this account still owes: one entry per allow-list
 * that has assessments ticked while the account carries none of its
 * codes. Derived from the form, so it survives a move between sections
 * and reports stored data the user never revisited — the CPT section
 * shows it without being told, and it clears the moment a code is
 * added.
 */
export const missingClaimCpts = (values, effectiveKeys = {}) => {
  const services = values?.ispConfiguration?.services;
  return Object.entries(CLAIM_CPT_CODES)
    .filter(([field]) => countEffective(values?.[field], effectiveKeys[field]) > 0)
    .filter(([, codes]) => !isClaimCptUsable(services, codes))
    .map(([field, codes]) => ({ field, codes }));
};

/**
 * Codes as they read in a sentence: "99422 or 99423". The joiner is
 * localized, so it is passed in rather than hardcoded here.
 */
export const formatClaimCptCodes = (codes, joiner) =>
  (codes || []).join(` ${String(joiner || 'or').trim()} `);
